/**
 * The single door between the UI and the platform.
 *
 * Every read and every write goes through here to the Node API, which is the
 * only thing that touches PostgreSQL. Bib numbers, receipt numbers, seat
 * checks and dues dates all need transactions; the `private` block is
 * stripped per viewer; derived stats are recomputed server-side. Nothing
 * about that rule changed when Firestore left — only the database behind the
 * API did.
 *
 * WHAT CHANGED ON THIS BRANCH (multi-tenant platform):
 *
 *   - Auth is a JWT from POST /auth/signin, kept in localStorage. Firebase is
 *     gone from the client entirely — including Storage: images now POST to
 *     /api/images (still resized on the canvas first by lib/image.js) and the
 *     API hands back a stable URL.
 *   - Club endpoints are scoped: /clubs/:clubId/… . Screens never see this —
 *     `setClubId` is called once when a club is selected, and every club
 *     method below builds its own path. The METHOD NAMES AND SIGNATURES ARE
 *     UNCHANGED, which is why ~30 screens did not have to move.
 *   - A new `platform` section carries the SRS surface: public plans, portal
 *     registration, invitations, the Super Admin console and the portal-admin
 *     screens.
 */

const API_URL = import.meta.env.VITE_API_URL ?? '/api';
const TOKEN_KEY = 'platform_jwt';

/**
 * The origin the API is served from, or '' when it shares ours.
 *
 * The API answers with ROOT-RELATIVE image paths — `/api/images/<key>` — and it
 * is right to. A URL it baked in would be wrong the day the domain changes, and
 * the same row is read by the browser, by mail templates and by the console.
 *
 * But a browser resolves a relative path against THE PAGE's origin, not the
 * API's. Serve the app on runnercrew.in and the API on api.runnercrew.in and
 * `/api/images/x` asks the STATIC SITE for that path — where the catch-all
 * rewrite that makes deep links work (`/*` → index.html) answers every unknown
 * path with the app shell and HTTP **200**. So a broken photo is not a broken
 * request: it is a successful one carrying an HTML document where a JPEG should
 * be. Nothing throws, nothing logs, every avatar is simply blank.
 *
 * So resolve them here, once, instead of in the ~19 places that render an
 * image — a list that only grows, and where a missed one fails silently.
 *
 * Empty in development, where VITE_API_URL is the same-origin `/api` path and
 * Vite's proxy already puts the two on one host. Then this is the identity.
 */
const API_ORIGIN = /^https?:\/\//i.test(API_URL) ? new URL(API_URL).origin : '';

/** Absolute-ise one value if it is an API-relative media path. */
function resolveMedia(value) {
  return typeof value === 'string' && value.startsWith('/api/')
    ? `${API_ORIGIN}${value}`
    : value;
}

/**
 * Walk a parsed response and absolute-ise every API-relative path in it.
 *
 * Deliberately shape-agnostic: it looks for the `/api/` prefix rather than for
 * field names, so a new endpoint returning a new image field is covered the day
 * it is written and nobody has to remember this file exists.
 */
function resolveMediaPaths(node) {
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const v = node[i];
      if (v && typeof v === 'object') resolveMediaPaths(v);
      else node[i] = resolveMedia(v);
    }
  } else if (node && typeof node === 'object') {
    for (const key of Object.keys(node)) {
      const v = node[key];
      if (v && typeof v === 'object') resolveMediaPaths(v);
      else node[key] = resolveMedia(v);
    }
  }
  return node;
}

/** The club whose id every club-scoped call carries. Set at selection time. */
let activeClubId = null;

export function setClubId(clubId) {
  activeClubId = clubId ?? null;
}

/** The public join form needs A club before anyone signs in. Seeded club 1. */
export const DEFAULT_CLUB_ID = import.meta.env.VITE_DEFAULT_CLUB_ID ?? '1';

function clubPath(path) {
  if (!activeClubId) throw new ApiError(0, 'NO_CLUB', 'No club is selected.');
  return `/clubs/${activeClubId}${path}`;
}

/* ------------------------------------------------------------------ *
 * The token
 * ------------------------------------------------------------------ */

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private windows: the session lives only as long as the tab. Fine.
  }
}

let onSessionLost = null;

/** Called when the API rejects our token, so the app can drop to sign-in. */
export function setSessionLostHandler(fn) {
  onSessionLost = fn;
}

/**
 * An error the UI can act on rather than just display. `code` is the API's
 * machine-readable reason — LINK_DEAD, CAPACITY_EXHAUSTED, PORTAL_READ_ONLY,
 * NO_PASSWORD, BAD_CREDENTIALS, CONFLICT…
 */
export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  get isAuthFailure() {
    return this.status === 401;
  }
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError(0, 'OFFLINE', 'Cannot reach the club right now. Check your connection.');
  }

  if (res.status === 204) return null;
  const payload = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new ApiError(
      res.status,
      payload.code ?? 'ERROR',
      payload.message ?? `Something went wrong (${res.status}).`,
    );
    if (err.isAuthFailure) onSessionLost?.();
    throw err;
  }
  return API_ORIGIN ? resolveMediaPaths(payload) : payload;
}

/**
 * Uploads a canvas-resized data URL and returns the stored image URL.
 * The replacement for Firebase Storage — one POST, one stable URL.
 */
export async function uploadImage(dataUrl) {
  const { url } = await request('/images', { method: 'POST', body: { dataUrl } });
  return url;
}

/* ------------------------------------------------------------------ *
 * The club — method names and signatures unchanged from the Firestore era
 * ------------------------------------------------------------------ */

export const api = {
  /** The whole selected club in one round trip. */
  loadClub: () => request(clubPath('')),

  /* ---------------- auth ---------------- */

  /**
   * PUBLIC. Mobile number OR email + password → { token, user, memberships,
   * ownedPortals }. The token goes straight into storage here so the very
   * next call is authenticated.
   */
  signIn: async (identifier, password) => {
    const payload = await request('/auth/signin', {
      method: 'POST',
      auth: false,
      body: { identifier, password },
    });
    setToken(payload.token);
    return payload;
  },

  /**
   * PUBLIC. Step one of signing in — the roster decides what to ask next:
   * `{ step: 'password' }`, `{ step: 'setup', confirm: { kind, hint } }` for a
   * first-timer, or `{ step: 'pending', message }` for a member the committee
   * has not activated yet.
   */
  identifyAccount: (identifier) =>
    request('/auth/identify', { method: 'POST', auth: false, body: { identifier } }),

  /**
   * PUBLIC. The first password, for an account that has never had one. Needs
   * the OTHER identity on their membership record as proof, and signs them in
   * on success — the same landing as accepting an invitation.
   */
  setFirstPassword: async (identifier, confirm, password) => {
    const payload = await request('/auth/first-password', {
      method: 'POST',
      auth: false,
      body: { identifier, confirm, password },
    });
    setToken(payload.token);
    return payload;
  },

  /** Restores a session: → { user, memberships, ownedPortals }. */
  session: () => request('/auth/session'),

  /** PUBLIC. → { sent, maskedEmail }. Sends the real email. */
  forgotPassword: (identifier) =>
    request('/auth/forgot-password', { method: 'POST', auth: false, body: { identifier } }),

  /** PUBLIC. Spends the one-time link and sets the password. */
  resetPassword: (token, password) =>
    request('/auth/reset-password', { method: 'POST', auth: false, body: { token, password } }),

  /** PUBLIC. What /invite/:token renders. */
  openInvite: (token) => request(`/auth/invite/${token}`, { auth: false }),

  /** PUBLIC. Accept the invitation: set the password, sign straight in. */
  acceptInvite: async (token, password) => {
    const payload = await request(`/auth/invite/${token}`, {
      method: 'POST',
      auth: false,
      body: { password },
    });
    setToken(payload.token);
    return payload;
  },

  /* ---------------- members ---------------- */

  addMember: (draft) => request(clubPath('/members'), { method: 'POST', body: draft }),
  activateMember: (id) => request(clubPath(`/members/${id}/activate`), { method: 'POST' }),
  updateMember: (id, patch) =>
    request(clubPath(`/members/${id}`), { method: 'PATCH', body: patch }),
  updateMe: (patch) => request(clubPath('/me'), { method: 'PATCH', body: patch }),
  setPhoto: (photo) => request('/auth/me/photo', { method: 'POST', body: { photo } }),
  removeMember: (id) => request(clubPath(`/members/${id}`), { method: 'DELETE' }),
  resendInvite: (id) => request(clubPath(`/members/${id}/resend-invite`), { method: 'POST' }),

  /** `current` is required only when a password already exists. */
  setOwnPassword: (current, password) =>
    request('/auth/password', { method: 'POST', body: { current, next: password } }),

  /* ---------------- money ---------------- */

  recordPayment: (memberId, amount, method, date) =>
    request(clubPath('/payments'), { method: 'POST', body: { memberId, amount, method, date } }),

  sendPayLink: (memberId) =>
    request(clubPath('/payments/link'), { method: 'POST', body: { memberId } }),

  addGround: (name) => request(clubPath('/grounds'), { method: 'POST', body: { name } }),
  removeGround: (id) => request(clubPath(`/grounds/${id}`), { method: 'DELETE' }),

  /** PUBLIC — the join form needs the same list a member sees. */
  publicGrounds: (clubId = DEFAULT_CLUB_ID) =>
    request(`/public/clubs/${clubId}/grounds`, { auth: false }),

  /** Records the member's own dues payment; `proof` is a screenshot data URL. */
  payOwnDues: (proof) =>
    request(clubPath('/me/dues'), { method: 'POST', body: { proof: proof ?? null } }),

  /** PUBLIC. What /pay/:token renders. Throws LINK_DEAD when spent. */
  openRenewalLink: (token) => request(`/renew/${token}`, { auth: false }),
  payRenewalLink: (token, proof) =>
    request(`/renew/${token}`, { method: 'POST', auth: false, body: { proof: proof ?? null } }),

  /* ---------------- results and hours ---------------- */

  addResult: (input) => request(clubPath('/results'), { method: 'POST', body: input }),
  importResults: (rows) =>
    request(clubPath('/results/import'), { method: 'POST', body: { rows } }),

  logVolunteerHours: (input) =>
    request(clubPath('/volunteer/hours'), { method: 'POST', body: input }),
  verifyHours: (logId) =>
    request(clubPath(`/volunteer/hours/${logId}/verify`), { method: 'POST' }),

  /* ---------------- events and volunteering ---------------- */

  addEvent: (input) => request(clubPath('/events'), { method: 'POST', body: input }),
  removeEvent: (id) => request(clubPath(`/events/${id}`), { method: 'DELETE' }),
  setEventCompleted: (id, completed) =>
    request(clubPath(`/events/${id}/complete`), { method: 'POST', body: { completed } }),
  setEventHead: (id, headId) =>
    request(clubPath(`/events/${id}/head`), { method: 'POST', body: { headId } }),
  /** The event's own web page. `null` or '' clears it. */
  setEventLink: (id, linkUrl) =>
    request(clubPath(`/events/${id}/link`), { method: 'POST', body: { linkUrl } }),
  /** { registeredCount, amountCollected, figuresPublic } — committee only. */
  setEventFigures: (id, figures) =>
    request(clubPath(`/events/${id}/figures`), { method: 'POST', body: figures }),
  toggleRsvp: (eventId, going) =>
    request(clubPath(`/events/${eventId}/rsvp`), { method: 'POST', body: { going } }),

  addDepartment: (eventId, input) =>
    request(clubPath(`/events/${eventId}/departments`), { method: 'POST', body: input }),
  removeDepartment: (eventId, deptId) =>
    request(clubPath(`/events/${eventId}/departments/${deptId}`), { method: 'DELETE' }),

  joinDepartment: (eventId, deptId) =>
    request(clubPath(`/events/${eventId}/departments/${deptId}/signup`), { method: 'POST' }),
  leaveDepartment: (eventId, deptId) =>
    request(clubPath(`/events/${eventId}/departments/${deptId}/signup`), { method: 'DELETE' }),

  confirmSignup: (signupId) =>
    request(clubPath(`/signups/${signupId}/confirm`), { method: 'POST' }),
  completeSignup: (signupId) =>
    request(clubPath(`/signups/${signupId}/complete`), { method: 'POST' }),

  /* ---------------- the join pipeline ---------------- */

  /** PUBLIC. A stranger asks to join a club. */
  submitJoinRequest: (draft, clubId = DEFAULT_CLUB_ID) =>
    request(`/public/clubs/${clubId}/requests`, { method: 'POST', auth: false, body: draft }),

  sendJoinLink: (requestId) =>
    request(clubPath(`/requests/${requestId}/link`), { method: 'POST' }),
  /**
   * The joining fee arrived outside the link — cash at the ground, a transfer
   * to the treasurer. Marks the request paid and allocates its receipt, which
   * is what lets Activate run.
   */
  recordJoinCash: (requestId, { amount, method, date } = {}) =>
    request(clubPath(`/requests/${requestId}/cash`), {
      method: 'POST',
      body: { amount, method, date },
    }),
  declineRequest: (requestId, reason) =>
    request(clubPath(`/requests/${requestId}/decline`), { method: 'POST', body: { reason } }),
  activateRequest: (requestId) =>
    request(clubPath(`/requests/${requestId}/activate`), { method: 'POST' }),

  /** PUBLIC. The one-time join link. `proof` is a payment-screenshot data URL. */
  openJoinLink: (token) => request(`/join/${token}`, { auth: false }),
  payJoinLink: (token, proof) =>
    request(`/join/${token}/pay`, { method: 'POST', auth: false, body: { proof: proof ?? null } }),
  onboardJoin: (token, payload) =>
    request(`/join/${token}/onboard`, { method: 'POST', auth: false, body: payload }),

  /* ---------------- messages and chat ---------------- */

  sendNotice: (text) => request(clubPath('/messages/notice'), { method: 'POST', body: { text } }),
  sendMemberNotice: (memberId, text) =>
    request(clubPath('/messages/member'), { method: 'POST', body: { memberId, text } }),
  sendSuggestion: (text) =>
    request(clubPath('/messages/suggestion'), { method: 'POST', body: { text } }),
  replyToMessage: (messageId, text) =>
    request(clubPath(`/messages/${messageId}/reply`), { method: 'POST', body: { text } }),
  markNoticesRead: () => request(clubPath('/messages/read-all'), { method: 'POST' }),

  /* Text only — chat images were retired on 27 Aug 2026. */
  sendChat: (text) => request(clubPath('/chat'), { method: 'POST', body: { text } }),
  markChatSeen: () => request(clubPath('/chat/seen'), { method: 'POST' }),

  /* ---------------- club settings ---------------- */

  setSafetyContact: (name, phone) =>
    request(clubPath('/safety'), { method: 'PATCH', body: { name, phone } }),

  /**
   * The payment setup every public link reads: collection mode (manual QR /
   * bank vs Razorpay), the structured account details, the QR, and — write-
   * only — the club's Razorpay key pair.
   */
  setPaymentDetails: (setup) =>
    request(clubPath('/payment-details'), { method: 'PATCH', body: setup }),

  /** The club's joining + renewal amounts per tier. Committee only. */
  setFees: (fees) => request(clubPath('/fees'), { method: 'PATCH', body: { fees } }),
};

/* ------------------------------------------------------------------ *
 * The platform — SRS surface. Used by the public site, the club selector,
 * the Super Admin console and the portal-admin screens.
 * ------------------------------------------------------------------ */

export const platform = {
  /* public site */
  plans: () => request('/public/plans', { auth: false }),
  /**
   * Creating a portal — INSTANT, no review, no payment: every portal starts
   * on the 15-day free trial and picks a paid plan from inside (owner's
   * direction, 6 Sep 2026). The reviewed paid-plan request that used to sit
   * beside this is retired from the UI; its route survives for records in
   * flight.
   */
  startTrial: (draft) =>
    request('/public/start-trial', { method: 'POST', auth: false, body: draft }),
  /** Who to chase about a request — the Super Admin's own name/email/mobile. */
  contact: () => request('/public/contact', { auth: false }),
  /** The published read-only demo logins. `{ enabled: false }` when unseeded. */
  demo: () => request('/public/demo', { auth: false }),
  /** "Talk to sales" — lands in the Super Admin console, not the plan queue. */
  salesEnquiry: (draft) =>
    request('/public/sales-enquiries', { method: 'POST', auth: false, body: draft }),

  /* portal admin (owner or super admin) */
  portal: (portalId) => request(`/portals/${portalId}`),
  createClub: (portalId, draft) =>
    request(`/portals/${portalId}/clubs`, { method: 'POST', body: draft }),
  updateClub: (portalId, clubId, patch) =>
    request(`/portals/${portalId}/clubs/${clubId}`, { method: 'PATCH', body: patch }),
  archiveClub: (portalId, clubId) =>
    request(`/portals/${portalId}/clubs/${clubId}/archive`, { method: 'POST' }),
  restoreClub: (portalId, clubId) =>
    request(`/portals/${portalId}/clubs/${clubId}/restore`, { method: 'POST' }),
  notifyArchivedMembers: (portalId, clubId, note) =>
    request(`/portals/${portalId}/clubs/${clubId}/notify-members`, {
      method: 'POST',
      body: { note },
    }),
  portalMembers: (portalId) => request(`/portals/${portalId}/members`),
  requestPayment: (portalId, type, planId) =>
    request(`/portals/${portalId}/payment-requests`, { method: 'POST', body: { type, planId } }),
  /** → { bank, payments }: the platform's bank details ride with the list. */
  portalPayments: (portalId) => request(`/portals/${portalId}/payments`),
  /** "I have paid": the screenshot (a data URL) and the UTR, on an open request. */
  uploadPaymentProof: (portalId, paymentId, proof, reference) =>
    request(`/portals/${portalId}/payments/${paymentId}/proof`, {
      method: 'POST',
      body: { proof, reference },
    }),
  /** Withdraw the owner's own unpaid request — the way back out of the pay desk. */
  cancelPayment: (portalId, paymentId) =>
    request(`/portals/${portalId}/payments/${paymentId}/cancel`, { method: 'POST' }),
  portalSubscription: (portalId) => request(`/portals/${portalId}/subscription`),
  portalTrace: (portalId) => request(`/portals/${portalId}/trace`),

  /* super admin console */
  sa: {
    overview: () => request('/sa/overview'),
    requests: (status) => request(`/sa/requests${status ? `?status=${status}` : ''}`),
    sendRequestPaymentLink: (id, amount) =>
      request(`/sa/requests/${id}/payment-link`, { method: 'POST', body: { amount } }),
    rejectRequest: (id, reason) =>
      request(`/sa/requests/${id}/reject`, { method: 'POST', body: { reason } }),
    salesEnquiries: () => request('/sa/sales-enquiries'),
    setEnquiryStatus: (id, status) =>
      request(`/sa/sales-enquiries/${id}/status`, { method: 'POST', body: { status } }),
    portals: () => request('/sa/portals'),
    /**
     * Create an admin portal outright, for a club the platform owner signed up
     * themselves. One call makes the owner account (plus an invitation to set
     * a password), the portal, the subscription and the settled payment.
     */
    createPortal: (draft) => request('/sa/portals', { method: 'POST', body: draft }),
    portalDetail: (id) => request(`/sa/portals/${id}`),
    suspendPortal: (id, reason) =>
      request(`/sa/portals/${id}/suspend`, { method: 'POST', body: { reason } }),
    reactivatePortal: (id) => request(`/sa/portals/${id}/reactivate`, { method: 'POST' }),
    sendPortalPaymentLink: (id, type, planId, amount) =>
      request(`/sa/portals/${id}/payment-link`, {
        method: 'POST',
        body: { type, planId, amount },
      }),
    plans: () => request('/sa/plans'),
    createPlan: (draft) => request('/sa/plans', { method: 'POST', body: draft }),
    updatePlan: (id, patch) => request(`/sa/plans/${id}`, { method: 'PATCH', body: patch }),
    payments: (status) => request(`/sa/payments${status ? `?status=${status}` : ''}`),
    markPaid: (id, reference, date, method) =>
      request(`/sa/payments/${id}/mark-paid`, {
        method: 'POST',
        body: { reference, date, method },
      }),
    /**
     * Money received outside the app — cash in hand, or a transfer already
     * seen on the statement. Books the row AND verifies it: the plan moves in
     * the same call, and the portal owner is emailed the confirmation.
     */
    recordPayment: (portalId, draft) =>
      request(`/sa/portals/${portalId}/record-payment`, { method: 'POST', body: draft }),
    verifyPayment: (id) => request(`/sa/payments/${id}/verify`, { method: 'POST' }),
    rejectPayment: (id, reason) =>
      request(`/sa/payments/${id}/reject`, { method: 'POST', body: { reason } }),
    /** The platform's own collection details — what every portal pays into. */
    bankDetails: () => request('/sa/bank-details'),
    setBankDetails: (draft) => request('/sa/bank-details', { method: 'PATCH', body: draft }),
    trace: (portalId) => request(`/sa/trace${portalId ? `?portalId=${portalId}` : ''}`),
    emails: (status) => request(`/sa/emails${status ? `?status=${status}` : ''}`),
    reports: () => request('/sa/reports'),
  },
};
